import React, { useEffect } from 'react';
import { useGate, useStore } from 'effector-react';
import { formatSize } from 'pano-utils';
import {
  ImageType,
  LibApiType,
  type LibItemMedia,
  type LibItemSysAudio,
  LibType,
} from '@types';
import { queryClient, queryKeys } from '@queryClient';
import addComponent from '@utils/addComponent';
import Title from '@components/Title';
import Button from '@components/Button';
import Image from '@components/Image';
import { $audioPlayer } from '@stores/audioPlayerStores';
import SearchBar from '../components/SearchBar';
import FooterClear from '../components/FooterClear';
import LibModalNav from '../layouts/LibModalNav';
import LibModalSpace from '../layouts/LibModalSpace';
import LibModal from '../layouts/LibModal';
import { $libAlbumId, LibItemsGate, LibMultipleGate } from '../stores/libState';
import libReset from '../actions/libReset';
import { $libSelected } from '../stores/libSelected';
import LibModalNavTip from '../layouts/LibModalNavTip';
import { LIB_SIZE_LIMIT } from '../constants';
import ListAudio from './ListAudio';
import UploadButtonMedia from '../components/UploadButtonMedia';
import onAlbumClick from '../actions/onAlbumClick';
import ListSysAudios from './ListSysAudios';
import { TTSModal } from '../TTSModal';
import { useLibRecent } from '../utils/libRecent';

type Props = {
  defaultNav?: 'lib' | 'sys';
  multiple?: boolean;
  noSysAudios?: boolean;
  onConfirm: (result: {
    libItems?: LibItemMedia[];
    sysItems?: LibItemSysAudio[];
  }) => void;
  onRequestClose: () => void;
};

function LibAudioModal({
  defaultNav = 'lib',
  multiple = false,
  noSysAudios = false,
  onConfirm,
  onRequestClose,
}: Props) {
  const [curNav, setCurNav] = useLibRecent(
    'LibAudioModal',
    noSysAudios ? 'lib' : defaultNav
  );
  useGate(LibMultipleGate, multiple);
  useEffect(() => {
    $audioPlayer.stopAudio();
    return () => {
      libReset();
      $audioPlayer.stopAudio();
    };
  }, []);
  const noSelected = !useStore($libSelected).length;
  const albumId = useStore($libAlbumId);

  const handleConfirm = () => {
    const selected = $libSelected.getState();
    if (curNav === 'sys') {
      const items = (LibItemsGate.state.getState() as LibItemSysAudio[])
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => selected.indexOf(a.id) - selected.indexOf(b.id));
      onConfirm({ sysItems: items });
    } else {
      const items = (LibItemsGate.state.getState() as LibItemMedia[])
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => selected.indexOf(a.id) - selected.indexOf(b.id));
      onConfirm({ libItems: items });
    }
  };

  const handleTTSClick = () => {
    addComponent((remove) => {
      return (
        <TTSModal
          albumId={albumId}
          onRequestClose={() => {
            queryClient.refetchQueries([queryKeys.libListAudio, 'items']);
            remove();
          }}
        />
      );
    });
  };

  return (
    <LibModal
      header={
        <>
          <Title>音频素材库</Title>
          {curNav === 'lib' && (
            <>
              <SearchBar />
              <Button
                scheme="green"
                className="ml-auto"
                onClick={handleTTSClick}
              >
                <Image
                  src="/imgs/btn-tts.54ebee6bc7a6d52bd3c635c4bca45894.png"
                  type={ImageType.sysMedia}
                  className="mr-5 h-16 w-16"
                />
                语音合成
              </Button>
              <UploadButtonMedia
                className="ml-10"
                libApiType={LibApiType.audio}
                onUploadDone={() => {
                  queryClient.refetchQueries([queryKeys.libListAudio, 'items']);
                }}
              />
            </>
          )}
        </>
      }
      footer={
        <>
          <Button disabled={noSelected} onClick={handleConfirm}>
            确认操作
          </Button>
          <FooterClear />
        </>
      }
      onRequestClose={onRequestClose}
    >
      <LibModalNav
        navItems={
          noSysAudios
            ? ([{ id: 'lib', name: '个人音频' }] as const)
            : ([
                { id: 'sys', name: '系统音乐' },
                { id: 'lib', name: '个人音频' },
              ] as const)
        }
        selected={curNav}
        onChange={(navType) => {
          onAlbumClick(0);
          setCurNav(navType);
        }}
      >
        {curNav === 'lib' && <LibModalSpace />}
        {curNav === 'lib' && (
          <LibModalNavTip>
            <p>格式：.mp3 音频文件</p>
            <p>{`容量：单个音频不超过${formatSize(
              LIB_SIZE_LIMIT[LibType.audio]
            )}`}</p>
          </LibModalNavTip>
        )}
      </LibModalNav>
      {curNav === 'lib' && <ListAudio />}
      {curNav === 'sys' && <ListSysAudios />}
    </LibModal>
  );
}

export default LibAudioModal;
